import { ContextType, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getFileTypeDescription } from '../../../utils/fileType';
import { MaterialIcon } from './MaterialIcon';
import Pill from '@pelckmans/elementis/components/pill';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import ChapterIcon from '@pelckmans/elementis/icons/chapter';
import FileIcon from '@pelckmans/elementis/icons/file';
import { Node } from '../../../api/table-of-content';
import { getFullHierarchyDescriptionOfNode } from '../../../utils/getNodeDescription';
import TableOfContentsContext from '../toc/_contexts/TableOfContentsContext';

type MaterialForTags = {
  node: string;
  lti?: {
    ltiResource: string;
    platform: string;
  };
  file?: {
    mimeType: string;
  };
  href?: string;
};

function findNodePath(nodes: Node[], nodeId: string): Node[] | null {
  function recursiveHelper(node: Node, path: Node[]): Node[] | null {
    const newPath = [...path, node];

    if (node.id === nodeId) return newPath;

    if (node.nodes && node.nodes.length > 0) {
      for (let child of node.nodes) {
        const result = recursiveHelper(child, newPath);

        if (result) return result;
      }
    }

    return null;
  }

  for (let item of nodes) {
    const result = recursiveHelper(item, []);

    if (result) return result;
  }

  return null;
}

type UserMaterialDialogHeaderTagsProps = {
  material: MaterialForTags;
  isTeacherAlike: boolean;
  nodes: ContextType<typeof TableOfContentsContext>['nodes'];
};

export function UserMaterialDialogHeaderTags(props: UserMaterialDialogHeaderTagsProps) {
  return (
    <PillGroup role={undefined} aria-label={undefined}>
      <UserMaterialTags {...props} />
    </PillGroup>
  );
}

type UserMaterialTagsProps = UserMaterialDialogHeaderTagsProps;

export function UserMaterialTags({ material, isTeacherAlike, nodes }: UserMaterialTagsProps) {
  const { t } = useTranslation();

  const tocLocation = useMemo(() => {
    const hierarchy = findNodePath(nodes, material.node);

    if (hierarchy) {
      return getFullHierarchyDescriptionOfNode(hierarchy, t);
    }
  }, [material, nodes, t]);

  const getFileType = () => {
    if ('href' in material && material.href) {
      return t('FILE_TYPE_DESCRIPTIONS.USER_MATERIAL.LINK');
    }

    if ('file' in material && material.file) {
      return t(`FILE_TYPE_DESCRIPTIONS.${getFileTypeDescription(material.file.mimeType).key}`);
    }

    if ('lti' in material && material.lti) {
      return t('FILE_TYPE_DESCRIPTIONS.USER_MATERIAL.LTI');
    }

    return undefined;
  };

  const fileType = getFileType();

  return (
    <>
      <Pill icon={<ChapterIcon />}>{tocLocation}</Pill>
      <Pill key="content-type" icon={<MaterialIcon materialType="user-material" size="xs" />}>
        {isTeacherAlike ? t('CONTENT_TYPE_DESCRIPTIONS.OWN_MATERIAL') : t('CONTENT_TYPE_DESCRIPTIONS.SHARED_MATERIAL')}
      </Pill>
      <>
        {fileType && (
          <Pill key="file-type" icon={<FileIcon />}>
            {fileType}
          </Pill>
        )}
      </>
    </>
  );
}
